import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Building2, Globe } from "lucide-react";

/**
 * Tenant-aware branding strip shown at the top of the company portal so every
 * page feels like the company's own workspace, not a generic SaaS shell.
 *
 * - Pulls /api/company/profile once on mount (company, subscription, plan).
 * - Shows the company logo (or an initials tile fallback), name, website,
 *   and a plan/status badge on the right.
 */
export default function CompanyBrandingBar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/company/profile")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, []);

  if (err) return null;

  const company = data?.company;
  const plan = data?.plan;

  const initials = (company?.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "•";

  return (
    <div
      className="bg-white border border-stone-200 border-l-4 border-l-amber-construction mb-6 flex items-center gap-5 px-5 py-4"
      data-testid="company-branding-bar"
    >
      {/* Logo / initials */}
      <div
        className="flex-shrink-0 w-14 h-14 bg-stone-950 flex items-center justify-center overflow-hidden"
        data-testid="company-branding-logo"
      >
        {company?.logo_url ? (
          <img
            src={company.logo_url}
            alt={company.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="text-amber-construction font-display font-black text-xl tracking-tight">
            {initials}
          </span>
        )}
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.25em] text-rebar uppercase mb-0.5 flex items-center gap-2">
          <Building2 size={11} />
          Company Workspace
        </div>
        <div
          className="font-display text-xl md:text-2xl font-bold text-graphite leading-tight truncate"
          data-testid="company-branding-name"
        >
          {company?.name || "—"}
        </div>
        {company?.website && (
          <a
            href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-mono text-rebar hover:text-brand-600 transition-colors"
            data-testid="company-branding-website"
          >
            <Globe size={11} />
            {company.website.replace(/^https?:\/\//, "")}
          </a>
        )}
      </div>

      {/* Plan / status badge */}
      <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0">
        {plan && (
          <div
            className="text-[10px] tracking-[0.2em] uppercase font-bold bg-graphite text-white px-2.5 py-1"
            data-testid="company-branding-plan"
          >
            {plan.name} plan
          </div>
        )}
        {company?.status && (
          <div
            className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 border ${
              company.status === "active"
                ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                : "border-stone-300 text-stone-600 bg-stone-50"
            }`}
            data-testid="company-branding-status"
          >
            {company.status}
          </div>
        )}
      </div>
    </div>
  );
}
